import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Person, Site, SiteEntry, SitePaging } from '@alfresco/js-api';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ProcessService } from '../../../../services/process.service';
import { WorkflowDefinitionModel } from '../../../../api/models/legacy/workflow-definition.model';
import { TASKS_STATUS, TasksQueryRequestData } from '../../../../api/models/task/tasks-query.model';
import { TasksDataService } from '../tasks-data.service';
import { Store } from '@ngrx/store';
import { AppStore, getUserProfile } from '@alfresco/aca-shared/store';
import { SitesService } from '@alfresco/adf-content-services';
import { map } from 'rxjs/operators';
import { LegacyApiService } from '../../../../api/services/legacy-api.service';
import { LegacyContentService } from '../../../../services/legacy-content.service';
import { DynamiclistsModel } from '../../../../api/models/epm/dynamiclists.model';

@Component({
  selector: 'epm-task-filter',
  templateUrl: './tasks-filter.component.html',
  styleUrls: ['./tasks-filter.component.scss']
})
export class TasksFilterComponent implements OnInit, OnDestroy {
  isExpanded = false;
  isOpenedFirstTime = true;
  taskFilterForm: FormGroup = new FormGroup({
    processDefinitionKey: new FormControl(''),
    status: new FormControl(TASKS_STATUS.ACTIVE),
    assignee: new FormControl(''),
    priority: new FormControl<number>(null, [Validators.min(1), Validators.max(3)]),
    startedBefore: new FormControl(null),
    startedAfter: new FormControl(null),
    // Plan-aligned fields
    site: new FormControl(''),
    plan: new FormControl(''),
    types: new FormArray([new FormControl('any')]),
    handover: new FormControl('')
  });

  currentDate = new Date();
  isAssigneeSearchOpen = false;
  processDefs: WorkflowDefinitionModel[];
  filteredProcessDefs: WorkflowDefinitionModel[];

  // Plan-aligned data sources
  sites: Site[];
  plans: any[] = [];
  types: { name: string; ref: string }[] = [];
  handovers: string[] = [];
  dynamicLists: DynamiclistsModel;

  private readonly onDestroy$ = new Subject<boolean>();
  constructor(
    private processService: ProcessService,
    public tasksDataService: TasksDataService,
    private store: Store<AppStore>,
    private sitesService: SitesService,
    private legacyApiService: LegacyApiService,
    private legacyContentService: LegacyContentService
  ) {}

  ngOnInit(): void {
    this.restoreState();
    this.filterProcessDefinitionKey.valueChanges.pipe(debounceTime(500), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.filteredProcessDefs = this._filterProcessDefs(value);
    });

    // if it is opened first time, auto-populate user, otherwise keep assignee value from state
    if (this.isOpenedFirstTime) {
      // Get user to auto populate assignee filter to create "My Tasks"
      this.store
        .select(getUserProfile)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((profile) => {
          if (profile.id) {
            this.filterAssignee.setValue(profile.id);
            this.tasksDataService.filters$.next(this.createTaskQueryRequestData());
          }
        });
    } else {
      this.tasksDataService.filters$.next(this.createTaskQueryRequestData());
    }

    this._setFilterProcessDefinitionKey('activiti$issueWorkflow');

    // Load supplemental data for plan-aligned filters
    this._fetchSites();
    this._initTypesAndHandovers();

    // React on site change to load plans, filter types by site, and reset selections/handovers
    this.filterSite.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((siteId: string) => {
      if (siteId) {
        this._fetchPlans(siteId);
      } else {
        this.plans = [];
      }
      // Filter available types by site and reset selection
      if (this.dynamicLists?.data) {
        this.types = this._filterProcessTypesBySiteId(this.dynamicLists).map((type) => ({ name: type.name, ref: type.ref }));
      } else {
        this.types = [];
      }
      this.typesFormArray.clear();
      this.typesFormArray.push(new FormControl('any'));
      // Reset handovers when site changes
      this.handovers = [];
      this.filterHandover.setValue('');
    });

    // React on types change to compute handovers list (union if none/any, intersection otherwise)
    this.typesFormArray.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((values: string[]) => {
      const lastType = values[values.length - 1];
      if (lastType && lastType !== 'any' && this.typesFormArray.length < this.types.length + 1) {
        this.typesFormArray.push(new FormControl(''));
      }

      const selectedRefs = values.filter((type: string) => !!type && type !== 'any');
      const includeAny = values.includes('any');
      const allowedRefsForSite = this._getAllowedTypeRefsForCurrentSite();
      const refsToLoad = includeAny ? allowedRefsForSite : selectedRefs;

      if (refsToLoad.length === 0) {
        this.handovers = [];
        this.filterHandover.setValue('');
        return;
      }

      Promise.all(refsToLoad.map((ref) => this.legacyApiService.retrieveUxData(ref))).then((uxDataArray) => {
        const handoverArrays = uxDataArray.map((ux) => ux.handoverOptions || []);
        const result = includeAny
          ? Array.from(new Set(handoverArrays.flat()))
          : ((): string[] => {
              const handoverSets = handoverArrays.map((arr) => new Set(arr));
              return handoverSets.length ? [...handoverSets[0]].filter((opt) => handoverSets.every((s) => s.has(opt))) : [];
            })();
        this.handovers = result;
        const cur = this.filterHandover.value;
        if (cur && !result.includes(cur)) {
          this.filterHandover.setValue('');
        }
      });
    });
  }

  onFiltersFormSubmit() {
    if (this.taskFilterForm.valid) {
      const tasksQuery = this.createTaskQueryRequestData();
      this.tasksDataService.filters$.next(tasksQuery);
      this.isExpanded = false;
    }
  }

  private createTaskQueryRequestData() {
    const tasksQuery = new TasksQueryRequestData();
    if (this.filterProcessDefinitionKey.value) {
      tasksQuery.processDefinitionKey = this.filterProcessDefinitionKey.value;
    }
    if (this.filterStatus.value) {
      tasksQuery.status = this.filterStatus.value;
    }
    if (this.filterAssignee.value) {
      tasksQuery.assignee = this.filterAssignee.value;
    }
    if (this.filterPriority.value) {
      tasksQuery.priority = this.filterPriority.value;
    }
    if (this.filterStartedBefore.value) {
      tasksQuery.startedBefore = this.filterStartedBefore.value;
    }
    if (this.filterStartedAfter.value) {
      tasksQuery.startedAfter = this.filterStartedAfter.value;
    }
    if (this.filterSite.value) {
      tasksQuery.site = this.filterSite.value;
    }
    if (this.filterPlan.value) {
      tasksQuery.plan = this.filterPlan.value;
    }
    // Build type names string from selected refs
    const typesValue = this.typesFormArray.value as string[];
    const selectedRefs = typesValue.filter((t: string) => t && t !== 'any');
    const selectedNames = selectedRefs.map((ref) => this.dynamicLists?.data?.find((dl) => dl.ref === ref)?.name).filter((n): n is string => !!n);
    const typeValue = selectedNames.length === 0 ? 'any' : selectedNames.join('|');
    if (typeValue !== 'any') {
      tasksQuery.type = typeValue;
      tasksQuery.types = selectedNames;
    }
    if (this.filterHandover.value) {
      tasksQuery.handover = this.filterHandover.value;
    }
    return tasksQuery;
  }

  onAssigneeSelect(person: Person) {
    this.taskFilterForm.patchValue({ assignee: person.id });
    this.isAssigneeSearchOpen = false;
  }

  resetForm() {
    this.taskFilterForm.reset({
      status: TASKS_STATUS.ACTIVE,
      types: ['any'],
      handover: ''
    });
    this.typesFormArray.clear();
    this.typesFormArray.push(new FormControl('any'));
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
    this.tasksDataService.filterState = {
      taskFilterFormValue: this.taskFilterForm.value,
      processDefs: this.processDefs
    };
  }

  processDefAutoOptionDisplay(processDefValue: string): string {
    return processDefValue && this.processDefs ? this.processDefs.find((_) => _.name === processDefValue).title : '';
  }

  getDateFormatString(): string {
    return 'DD/MM/YYYY';
  }

  private _loadProcessDefs() {
    this.processService
      .getLegacyProcessDefinitions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((processDefs) => {
        this.processDefs = processDefs.data ? processDefs.data : [];
        this.filteredProcessDefs = this.processDefs;
      });
  }

  private _filterProcessDefs(value: string): any {
    if (this.processDefs) {
      const filterValue = value.toLowerCase();
      return this.processDefs.filter(
        (def) => def.name.toLowerCase().includes(filterValue) || (def.title && def.title.toLowerCase().includes(filterValue))
      );
    }
    return [];
  }

  private restoreState() {
    if (this.tasksDataService.filterState) {
      this.isOpenedFirstTime = false;
      const prevState = this.tasksDataService.filterState;
      if (prevState.taskFilterFormValue) {
        this.taskFilterForm.setValue(prevState.taskFilterFormValue);
      }
      if (prevState.processDefs) {
        this.processDefs = prevState.processDefs;
      }
      this.tasksDataService.filters$.next(this.createTaskQueryRequestData());
    } else {
      this._loadProcessDefs();
    }
  }

  private _setFilterProcessDefinitionKey(key: string) {
    this.filterProcessDefinitionKey.setValue(key);
  }

  get filterProcessDefinitionKey(): AbstractControl {
    return this.taskFilterForm.get('processDefinitionKey');
  }

  get filterStatus(): AbstractControl {
    return this.taskFilterForm.get('status');
  }

  get filterAssignee(): AbstractControl {
    return this.taskFilterForm.get('assignee');
  }

  get filterPriority(): AbstractControl {
    return this.taskFilterForm.get('priority');
  }

  get filterStartedAfter(): AbstractControl {
    return this.taskFilterForm.get('startedAfter');
  }

  get filterStartedBefore(): AbstractControl {
    return this.taskFilterForm.get('startedBefore');
  }

  get filterHandover(): AbstractControl {
    return this.taskFilterForm.get('handover');
  }

  get filterSite(): AbstractControl {
    return this.taskFilterForm.get('site');
  }

  get filterPlan(): AbstractControl {
    return this.taskFilterForm.get('plan');
  }

  get typesFormArray(): FormArray {
    return this.taskFilterForm.get('types') as FormArray;
  }

  getAvailableTypes(index: number): { name: string; ref: string }[] {
    const selectedTypes = this.typesFormArray.value.filter((type: string, i: number) => type && type !== 'any' && i !== index);
    return this.types.filter((type) => !selectedTypes.includes(type.ref));
  }

  removeTypeField(index: number): void {
    if (this.typesFormArray.length > 1) {
      this.typesFormArray.removeAt(index);
    }
  }

  getTypeControl(index: number): FormControl {
    return this.typesFormArray.at(index) as FormControl;
  }

  private _fetchSites() {
    this.sitesService
      .getSites()
      .pipe(map((sitePaging: SitePaging) => sitePaging.list.entries.map((siteEntry: SiteEntry) => siteEntry.entry)))
      .subscribe((sites: Site[]) => {
        this.sites = sites;
      });
  }

  private _fetchPlans(siteid: string) {
    // Reuse legacy API used in plan filter
    if (siteid) {
      this.legacyContentService.getIssuePlans(siteid).subscribe((plans) => {
        this.plans = plans.map((plan) => ({ ...plan, name: this._removeFileExtension(plan.name) }));
      });
    }
  }

  private _initTypesAndHandovers() {
    this.legacyApiService.fetchWfTypes('epmIssueWf:issueWorkflowDataListItem').then((dynamicList) => {
      this.dynamicLists = dynamicList;
      this.types = this._filterProcessTypesBySiteId(dynamicList).map((type) => ({ name: type.name, ref: type.ref }));
      this.typesFormArray.setValue(['any']);
    });
  }

  private _removeFileExtension(fileName: string): string {
    return fileName ? fileName.replace(/\.[^/.]+$/, '') : fileName;
  }

  private _filterProcessTypesBySiteId(processTypes: DynamiclistsModel) {
    const siteId = this.filterSite.value;
    if (siteId) {
      return processTypes.data.filter((type) => type.siteid === siteId);
    }
    // No site selected: do not expose any types
    return [];
  }

  private _getAllowedTypeRefsForCurrentSite(): string[] {
    if (!this.dynamicLists?.data) {
      return [];
    }
    const siteId = this.filterSite.value;
    const list = siteId ? this.dynamicLists.data.filter((t) => t.siteid === siteId) : this.dynamicLists.data;
    return list.map((t) => t.ref);
  }
}
